import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Menu, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Sidebar } from "./Sidebar";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, ROLE_LABEL } from "../lib/api";

const Topbar = ({ user, onMenu, onLogout }) => (
  <header className="sticky top-0 z-40 h-16 bg-[#030a1f]/80 backdrop-blur-xl border-b border-[#1e3a8a]/70 flex items-center justify-between px-4 sm:px-6">
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#38bdf8]/60 to-transparent" />
    <div className="flex items-center gap-2 sm:gap-3">
      <Button variant="ghost" size="icon" className="lg:hidden h-10 w-10" onClick={onMenu} data-testid="mobile-menu-button" aria-label="Menu">
        <Menu className="h-5 w-5" />
      </Button>
      <img src="/logo-siber.png" alt="" className="h-8 w-auto sm:hidden" />
      <div className="leading-tight sm:hidden">
        <div className="font-heading font-black text-sm">SILOG <span className="text-[#38bdf8]">SIBER</span></div>
        <div className="text-[10px] text-muted-foreground truncate max-w-[150px]">{user?.polda || ROLE_LABEL[user?.role]}</div>
      </div>
      <div className="hidden sm:block">
        <div className="eyebrow">Bareskrim Polri</div>
        <div className="font-heading font-bold text-sm">Direktorat Tindak Pidana Siber</div>
      </div>
    </div>
    <div className="flex items-center gap-2 sm:gap-3">
      <div className="text-right leading-tight hidden sm:block" data-testid="current-user-info">
        <div className="text-sm font-medium truncate max-w-[180px]">{user?.name || user?.email}</div>
        <div className="text-[11px] text-muted-foreground">
          {ROLE_LABEL[user?.role]}{user?.polda ? ` · ${user.polda}` : ""}
        </div>
      </div>
      <Avatar className="h-9 w-9 rounded-sm">
        <AvatarImage src={user?.picture} />
        <AvatarFallback className="rounded-sm">{(user?.name || user?.email || "?")[0].toUpperCase()}</AvatarFallback>
      </Avatar>
      <Button variant="outline" size="icon" className="h-10 w-10 sm:h-9 sm:w-9" onClick={onLogout} data-testid="logout-button" title="Keluar" aria-label="Keluar">
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  </header>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:block w-64 shrink-0 sticky top-0 h-screen">
        <Sidebar meta={meta} user={user} />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-64 border-0">
          <SheetTitle className="sr-only">Navigasi</SheetTitle>
          <Sidebar meta={meta} user={user} onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar user={user} onMenu={() => setOpen(true)} onLogout={handleLogout} />
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {meta ? <Outlet context={{ meta }} /> : <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        </main>
      </div>
    </div>
  );
}
